import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { useSelector } from "react-redux";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import Spinner from "../../../components/common/spinner/Spinner";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import "../../Style.css";

const AddCustomerLead = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [purposeOptions, setPurposeOptions] = useState([]);

  useEffect(() => {
    const fetchPurposes = async () => {
      try {
        const response = await axios.post(`${baseUrl}/property/purposes`, {
          page_number: 1,
          page_size: 100,
          order_by: "ASC",
        });
        if (response.data.statusType === "SUCCESS" && response.data.propertyPurposes) {
          const options = response.data.propertyPurposes.map((p) => ({
            value: p.id,
            label: p.name,
          }));
          setPurposeOptions(options);
        }
      } catch (error) {
        console.error("Error fetching purposes:", error);
      }
    };
    fetchPurposes();
  }, [baseUrl]);

  const initialValues = {
    title: "",
    description: "",
    budget: "",
    city: "",
    state: "",
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    budget: Yup.number()
      .typeError("Budget must be a number")
      .required("Budget is required")
      .positive("Budget must be a positive number"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!selectedPurpose) {
      showToast("Please select a purpose", { type: "error" });
      setSubmitting(false);
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        title: values.title,
        description: values.description,
        budget: Number(values.budget),
        city: values.city,
        state: values.state,
        purpose: {
          id: selectedPurpose.value,
        },
      };

      const response = await axios.post(`${baseUrl}/customer-lead/add`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.statusType === "SUCCESS") {
        showToast("Customer Lead saved successfully!", { type: "success" });
        setTimeout(() => {
          navigate("/customer-lead");
        }, 2000);
      } else {
        showToast("Failed to add customer lead", { type: "error" });
      }
    } catch (error) {
      console.error("Error adding customer lead:", error);
      showToast("Error occurred while adding customer lead", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      color: '#000',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
    }),
    option: (provided, state) => ({
      ...provided,
      color: '#000',
      backgroundColor: state.isFocused ? '#e9ecef' : '#fff',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000',
    })
  };

  return (
    <Container style={{ marginTop: "2rem" }}>
      <Notification />
      <SingleDetailHeader header="Add Lead" description="Add a new customer lead" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <Field
                    type="text"
                    className="form-control"
                    id="title"
                    placeholder="Enter Title"
                    name="title"
                  />
                  <ErrorMessage name="title" component="div" className="text-danger" />
                </div>
              </Col>

              <Col md={12}>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <Field
                    as="textarea"
                    className="form-control"
                    id="description"
                    placeholder="Enter Description"
                    name="description"
                    rows="3"
                  />
                  <ErrorMessage name="description" component="div" className="text-danger" />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label htmlFor="budget" className="form-label">
                    Budget
                  </label>
                  <Field
                    type="number"
                    className="form-control"
                    id="budget"
                    placeholder="Enter Budget"
                    name="budget"
                  />
                  <ErrorMessage name="budget" component="div" className="text-danger" />
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Purpose</label>
                  <Select
                    options={purposeOptions}
                    onChange={setSelectedPurpose}
                    value={selectedPurpose}
                    placeholder="Select Purpose"
                    styles={customStyles}
                    menuPortalTarget={document.body}
                  />
                  {/* Validation for purpose is handled manually in handleSubmit */}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label htmlFor="city" className="form-label">
                    City
                  </label>
                  <Field
                    type="text"
                    className="form-control"
                    id="city"
                    placeholder="Enter City"
                    name="city"
                  />
                  <ErrorMessage name="city" component="div" className="text-danger" />
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <label htmlFor="state" className="form-label">
                    State
                  </label>
                  <Field
                    type="text"
                    className="form-control"
                    id="state"
                    placeholder="Enter State"
                    name="state"
                  />
                  <ErrorMessage name="state" component="div" className="text-danger" />
                </div>
              </Col>
            </Row>

            <button
              type="submit"
              className="btn btn-primary mt-4 mb-5"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner loading={true} /> : "Add Customer Lead"}
            </button>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default AddCustomerLead;
